/**
 * CP-11.2 / Milestone 3 — Escrow read-only frontend.
 *
 * Per Phase 11 r2 §CP-11.2 + Lock §13 (no manual money movement at UI):
 *  - Every component below is read-only. No onClick mutators on escrow.
 *  - Audience projection is preserved structurally by the backend; this UI
 *    additionally never displays admin-only keys (provider_charge_id,
 *    provider_name, failure_reason) outside the AdminEscrowsTab.
 *  - ReleaseTimeline surfaces Lock §3's three conditions in fixed order.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

const TIDS = {
  badge: "escrow-state-badge",
  timeline: "escrow-release-timeline",
  conditionRow: "escrow-condition-row",
  clientPanel: "client-escrow-panel",
  leadPanel: "lead-escrow-panel",
  adminTab: "admin-escrows-tab",
  adminTable: "admin-escrows-table",
  adminRow: "admin-escrows-row",
  adminStateFilter: "admin-escrows-state-filter",
  adminRefresh: "admin-escrows-refresh",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function money(amount, currency) {
  if (amount == null) return "—";
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  return `${currency || ""} ${n.toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// EscrowStateBadge — small pill: pending_capture / held / released / refunded / failed
// ---------------------------------------------------------------------------

const STATE_TONES = {
  pending_capture: "bg-amber-50 text-amber-800 border-amber-200",
  held: "bg-sky-50 text-sky-800 border-sky-200",
  released: "bg-emerald-50 text-emerald-800 border-emerald-200",
  refunded: "bg-slate-100 text-slate-700 border-slate-200",
  failed: "bg-rose-50 text-rose-800 border-rose-200",
};

export function EscrowStateBadge({ state }) {
  const tone = STATE_TONES[state] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span
      data-testid={TIDS.badge}
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${tone}`}
    >
      {state}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ReleaseTimeline — Lock §3 three conditions (read-only)
// ---------------------------------------------------------------------------

export function ReleaseTimeline({ eligibility }) {
  if (!eligibility) return null;
  return (
    <div data-testid={TIDS.timeline} className="mt-3 border-t pt-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[var(--avcp-ink-soft)]">
          Release conditions
        </span>
        <span
          className={`text-xs font-semibold ${
            eligibility.eligible ? "text-emerald-700" : "text-amber-700"
          }`}
        >
          {eligibility.eligible ? "All clear" : "Awaiting"}
        </span>
      </div>
      <ul className="space-y-1.5">
        {(eligibility.conditions || []).map((c) => (
          <li
            key={c.key}
            data-testid={`${TIDS.conditionRow}-${c.key}`}
            className="flex items-start gap-2 text-sm"
          >
            <span
              aria-hidden
              className={`mt-0.5 inline-block w-4 h-4 rounded-full text-white text-[10px] leading-4 text-center font-bold ${
                c.satisfied ? "bg-emerald-500" : "bg-slate-300"
              }`}
            >
              {c.satisfied ? "✓" : "·"}
            </span>
            <span className="flex-1">
              <div className={c.satisfied ? "text-slate-800" : "text-slate-600"}>
                {c.label}
              </div>
              <div className="text-xs text-[var(--avcp-ink-soft)]">{c.detail}</div>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClientEscrowPanel — Client surface for a single booking
// ---------------------------------------------------------------------------

export function ClientEscrowPanel({ jobId, bookingId }) {
  const [escrow, setEscrow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api(`/jobs/${jobId}/bookings/${bookingId}/escrow`);
        if (!cancelled) setEscrow(r);
      } catch (e) {
        if (!cancelled) setMissing(e.status === 404);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, bookingId]);
  if (loading) return null;
  if (missing || !escrow) return null;
  return (
    <div className="avcp-card p-5" data-testid={TIDS.clientPanel}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold">Payment in escrow</h3>
        <EscrowStateBadge state={escrow.state} />
      </div>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Funds are held by the platform and released to your Lead Technician once
        the release conditions are met. This view is read-only.
      </p>
      <div className="text-sm">
        <span className="font-semibold">{money(escrow.amount, escrow.currency)}</span>
        {escrow.captured_at && (
          <span className="text-xs text-[var(--avcp-ink-soft)] ml-2">
            captured {fmt(escrow.captured_at)}
          </span>
        )}
      </div>
      <ReleaseTimeline eligibility={escrow.eligibility} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// LeadEscrowPanel — Lead surface for a single booking (own bookings)
// ---------------------------------------------------------------------------

export function LeadEscrowPanel({ bookingId }) {
  const [escrow, setEscrow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api(`/leads/me/bookings/${bookingId}/escrow`);
        if (!cancelled) setEscrow(r);
      } catch (e) {
        if (!cancelled) setMissing(e.status === 404);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);
  if (loading) return null;
  if (missing || !escrow) return null;
  return (
    <div className="avcp-card p-5" data-testid={TIDS.leadPanel}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold">Escrow status</h3>
        <EscrowStateBadge state={escrow.state} />
      </div>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Payment is held by the platform. Release is automatic once all three
        conditions below are satisfied. No manual action is available — the
        platform enforces release rules.
      </p>
      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-xs text-[var(--avcp-ink-soft)]">Total in escrow</div>
          <div className="font-semibold">{money(escrow.amount, escrow.currency)}</div>
        </div>
        <div>
          <div className="text-xs text-[var(--avcp-ink-soft)]">Your share</div>
          <div className="font-semibold">{money(escrow.lead_amount, escrow.currency)}</div>
        </div>
        <div>
          <div className="text-xs text-[var(--avcp-ink-soft)]">Platform fee</div>
          <div className="font-semibold">{money(escrow.platform_fee, escrow.currency)}</div>
        </div>
      </div>
      {escrow.released_at && (
        <p className="text-xs text-emerald-700 mt-2">
          Released {fmt(escrow.released_at)} · transfer {escrow.provider_transfer_id}
        </p>
      )}
      <ReleaseTimeline eligibility={escrow.eligibility} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdminEscrowsTab — read-only observability table (Lock §13)
// ---------------------------------------------------------------------------

const ADMIN_STATE_OPTIONS = [
  "", "pending_capture", "held", "released", "refunded", "failed",
];

export function AdminEscrowsTab() {
  const [rows, setRows] = useState([]);
  const [stateFilter, setStateFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const qs = stateFilter ? `?state=${stateFilter}&limit=200` : "?limit=200";
        const r = await api(`/admin/escrows${qs}`);
        if (!cancelled) setRows(r.data || []);
      } catch { if (!cancelled) setRows([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [stateFilter, tick]);
  return (
    <div data-testid={TIDS.adminTab} className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm">
          State:&nbsp;
          <select
            data-testid={TIDS.adminStateFilter}
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            {ADMIN_STATE_OPTIONS.map((s) => (
              <option key={s || "any"} value={s}>{s || "(any)"}</option>
            ))}
          </select>
        </label>
        <button
          data-testid={TIDS.adminRefresh}
          onClick={() => setTick((t) => t + 1)}
          className="avcp-btn-secondary text-sm"
        >
          Refresh
        </button>
        <span className="text-xs text-[var(--avcp-ink-soft)]">
          Read-only · {rows.length} row(s)
        </span>
      </div>
      <div className="overflow-x-auto">
        <table data-testid={TIDS.adminTable} className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-[var(--avcp-ink-soft)]">
              <th className="py-2 pr-3">Invoice</th>
              <th className="py-2 pr-3">Booking</th>
              <th className="py-2 pr-3">State</th>
              <th className="py-2 pr-3">Amount</th>
              <th className="py-2 pr-3">Provider</th>
              <th className="py-2 pr-3">Charge ID</th>
              <th className="py-2 pr-3">Captured</th>
              <th className="py-2 pr-3">Released</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="py-3 text-[var(--avcp-ink-soft)]">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="py-3 text-[var(--avcp-ink-soft)]">No escrow rows.</td></tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.invoice_id}
                data-testid={`${TIDS.adminRow}-${r.invoice_id}`}
                className="border-t"
              >
                <td className="py-2 pr-3">#{r.invoice_id}</td>
                <td className="py-2 pr-3">#{r.booking_id}</td>
                <td className="py-2 pr-3"><EscrowStateBadge state={r.state} /></td>
                <td className="py-2 pr-3">{money(r.amount, r.currency)}</td>
                <td className="py-2 pr-3">{r.provider_name || "—"}</td>
                <td className="py-2 pr-3 font-mono text-xs">{r.provider_charge_id || "—"}</td>
                <td className="py-2 pr-3 text-xs">{fmt(r.captured_at)}</td>
                <td className="py-2 pr-3 text-xs">{fmt(r.released_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
